import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { useNavigate } from 'react-router-dom';
const DEFAULT_ITEMS = [{
  label: 'home',
  href: '/',
  rotation: -4,
  hoverStyles: {
    bgColor: '#3b82f6',
    textColor: '#ffffff'
  }
}, {
  label: 'projects',
  href: '/projects',
  rotation: 4,
  hoverStyles: {
    bgColor: '#f59e0b',
    textColor: '#ffffff'
  }
}, {
  label: 'about',
  href: '/about',
  rotation: -2,
  hoverStyles: {
    bgColor: '#10b981',
    textColor: '#ffffff'
  }
}, {
  label: 'thoughts',
  href: '/notes',
  rotation: 1,
  hoverStyles: {
    bgColor: '#ec4899',
    textColor: '#ffffff'
  }
}, {
  label: 'contact',
  href: '/contact',
  rotation: 3,
  hoverStyles: {
    bgColor: '#8b5cf6',
    textColor: '#ffffff'
  }
}];
export default function BubbleMenu({
  logo,
  onMenuClick,
  items = DEFAULT_ITEMS,
  menuBg = '#fff',
  menuContentColor = '#000'
}: any) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const navigate = useNavigate();
  const overlayRef = useRef(null);
  const bubblesRef = useRef<any[]>([]);
  const labelRefs = useRef<any[]>([]);
  const handleToggle = () => {
    const nextState = !isMenuOpen;
    if (nextState) setShowOverlay(true);
    setIsMenuOpen(nextState);
    onMenuClick?.(nextState);
  };
  const handleLogoClick = () => {
    setIsMenuOpen(false);
    navigate('/');
  };
  const handleLinkClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    // Allow animation to start before navigating
    setTimeout(() => {
      navigate(href);
    }, 300);
  };
  useEffect(() => {
    const overlay = overlayRef.current;
    const bubbles = bubblesRef.current.filter(Boolean);
    const labels = labelRefs.current.filter(Boolean);
    if (!overlay || !bubbles.length) return;
    if (isMenuOpen) {
      gsap.set(overlay, {
        display: 'flex',
        opacity: 0
      });
      gsap.to(overlay, {
        opacity: 1,
        duration: 0.4
      });
      gsap.set(bubbles, {
        scale: 0,
        rotation: 0
      });
      gsap.set(labels, {
        y: 20,
        opacity: 0
      });
      bubbles.forEach((bubble, i) => {
        const tl = gsap.timeline({
          delay: i * 0.1
        });
        tl.to(bubble, {
          scale: 1,
          rotation: items[i]?.rotation || 0,
          duration: 0.6,
          ease: 'back.out(1.7)'
        });
        tl.to(labels[i], {
          y: 0,
          opacity: 1,
          duration: 0.4
        }, '-=0.4');
      });
    } else if (showOverlay) {
      gsap.to(overlay, {
        opacity: 0,
        duration: 0.3,
        onComplete: () => {
          setShowOverlay(false);
          gsap.set(overlay, {
            display: 'none'
          });
        }
      });
    }
  }, [isMenuOpen, showOverlay, items]);
  return <>
      {/* Logo — fixed top-left */}
      <button type="button" className="bubble logo-bubble cursor-target" onClick={handleLogoClick} aria-label="Go to homepage" style={{
      background: menuBg
    }} data-id="element-24">
        <div className="logo-content" data-id="element-25">{logo}</div>
      </button>

      {/* Toggle — fixed top-right */}
      <button type="button" className={`bubble toggle-bubble menu-btn cursor-target ${isMenuOpen ? 'open' : ''}`} onClick={handleToggle} aria-label="Toggle menu" aria-pressed={isMenuOpen} style={{
      background: menuBg
    }} data-id="element-26">
        <span className="menu-line" style={{
        background: menuContentColor
      }} data-id="element-27" />
        <span className="menu-line" style={{
        background: menuContentColor
      }} data-id="element-28" />
      </button>

      {showOverlay && <div ref={overlayRef} className="bubble-menu-items" data-id="element-29">
          <ul className="pill-list" role="menu" data-id="element-30">
            {items.map((item: any, idx: number) => <li key={idx} className="pill-col" data-id="element-31">
                <a role="menuitem" href={item.href} className="pill-link cursor-target" onClick={e => handleLinkClick(e, item.href)} style={{
            '--item-rot': `${item.rotation}deg`,
            '--hover-bg': item.hoverStyles?.bgColor,
            '--hover-color': item.hoverStyles?.textColor
          } as any} ref={el => {
            if (el) bubblesRef.current[idx] = el;
          }} data-id="element-32">
                  <span ref={el => {
              if (el) labelRefs.current[idx] = el;
            }} data-id="element-33">
                    {item.label}
                  </span>
                </a>
              </li>)}
          </ul>
        </div>}
      <style data-id="element-34">{`
        .logo-bubble {
          position: fixed;
          top: 1.5rem;
          left: 1.5rem;
          z-index: 101;
          padding: 0.75rem 1.5rem;
          height: 3.5rem;
          border-radius: 9999px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.2);
          transition: transform 0.2s ease;
          cursor: pointer;
        }
        .toggle-bubble {
          position: fixed;
          top: 1.5rem;
          right: 1.5rem;
          z-index: 101;
          width: 3.5rem;
          height: 3.5rem;
          border-radius: 9999px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 4px;
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.2);
          transition: transform 0.2s ease;
          cursor: pointer;
        }
        .logo-bubble:hover,
        .toggle-bubble:hover {
          transform: scale(1.05);
        }
        .menu-line {
          width: 1.25rem;
          height: 2px;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }
        .menu-btn.open .menu-line:first-child {
          transform: translateY(3px) rotate(45deg);
        }
        .menu-btn.open .menu-line:last-child {
          transform: translateY(-3px) rotate(-45deg);
        }
        .bubble-menu-items {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.8);
          backdrop-filter: blur(8px);
          z-index: 90;
          display: none;
          align-items: center;
          justify-content: center;
        }
        .pill-list {
          list-style: none;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          align-items: center;
        }
        .pill-link {
          display: block;
          padding: 1rem 3rem;
          background: white;
          color: black;
          border-radius: 9999px;
          font-weight: 800;
          font-size: 2rem;
          text-transform: uppercase;
          letter-spacing: -0.05em;
          text-decoration: none;
          transform: rotate(var(--item-rot));
          transition: background 0.3s, color 0.3s, transform 0.3s;
        }
        .pill-link:hover {
          background: var(--hover-bg);
          color: var(--hover-color);
          transform: rotate(0deg) scale(1.1);
        }
        @media (min-width: 768px) {
          .pill-list {
            flex-direction: row;
          }
        }
      `}</style>
    </>;
}